import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { StatusBadge, PageLoader, ErrorState, EmptyState, Modal, Field } from '../../components/ui';
import { storiesApi } from '../../api/resources';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../lib/format';

const STATUS_TABS = ['pending', 'draft', 'approved', 'published', 'rejected', ''];

export default function AdminStories() {
  const toast = useToast();
  const [status, setStatus] = useState('pending');
  const [state, setState] = useState({ loading: true, error: null, items: [] });
  const [review, setReview] = useState(null); // { story, action }
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setState((s) => ({ ...s, loading: true, error: null }));
    storiesApi
      .list({ status: status || undefined, per_page: 50 })
      .then((res) => setState({ loading: false, error: null, items: res.items }))
      .catch((error) => setState({ loading: false, error, items: [] }));
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  function openReview(story, action) {
    setReview({ story, action });
    setComment('');
  }

  async function runAction() {
    if (!review) return;
    const { story, action } = review;
    setBusy(true);
    try {
      await storiesApi[action](story.id, comment);
      toast.success(`Story ${action}d.`);
      setReview(null);
      load();
    } catch (err) {
      toast.error(err.message || `Could not ${action} story.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <Seo title="Manage Stories" />
      <h1 className="font-display text-2xl font-bold">Stories</h1>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t || 'all'}
            onClick={() => setStatus(t)}
            className={`pill cursor-pointer capitalize transition-colors ${
              status === t ? 'bg-maroon-700 text-white' : 'bg-surface-gray text-ink-soft hover:bg-cream-300'
            }`}
          >
            {t || 'All'}
          </button>
        ))}
      </div>

      {state.loading ? (
        <PageLoader />
      ) : state.error ? (
        <ErrorState error={state.error} onRetry={load} />
      ) : state.items.length === 0 ? (
        <EmptyState title="No stories in this view" />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-cream-300 bg-surface-alt text-xs uppercase tracking-wider text-ink-soft">
              <tr>
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Author</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Updated</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-300">
              {state.items.map((s) => (
                <tr key={s.id} className="hover:bg-surface-alt">
                  <td className="px-5 py-3 font-medium text-ink">
                    <Link to={`/stories/${s.slug || s.id}`} className="hover:text-maroon-700">
                      {s.title}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-ink-muted">{s.author?.name || '—'}</td>
                  <td className="px-5 py-3">
                    <StatusBadge status={s.status} />
                  </td>
                  <td className="px-5 py-3 text-ink-muted">{formatDate(s.updated_at || s.created_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-2">
                      {s.status === 'pending' && (
                        <>
                          <button onClick={() => openReview(s, 'approve')} className="btn-gold px-3 py-1.5 text-xs">
                            Approve
                          </button>
                          <button onClick={() => openReview(s, 'reject')} className="btn-ghost px-3 py-1.5 text-xs text-red-600">
                            Reject
                          </button>
                        </>
                      )}
                      {s.status === 'approved' && (
                        <button onClick={() => openReview(s, 'publish')} className="btn-primary px-3 py-1.5 text-xs">
                          Publish
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!review}
        onClose={() => setReview(null)}
        title={review ? `${review.action[0].toUpperCase()}${review.action.slice(1)} story` : ''}
        footer={
          <>
            <button onClick={() => setReview(null)} className="btn-ghost" disabled={busy}>
              Cancel
            </button>
            <button
              onClick={runAction}
              className={review?.action === 'reject' ? 'btn-danger' : 'btn-primary'}
              disabled={busy}
            >
              {busy ? 'Working…' : `Confirm ${review?.action}`}
            </button>
          </>
        }
      >
        {review && (
          <div className="space-y-4">
            <p className="text-sm text-ink-soft">
              <span className="font-medium">{review.story.title}</span> by {review.story.author?.name}
            </p>
            <Field label="Review comment" hint="Optional note added to the approval history.">
              <textarea
                className="input"
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={review.action === 'reject' ? 'Reason for rejection…' : 'Optional comment…'}
              />
            </Field>
          </div>
        )}
      </Modal>
    </div>
  );
}
